import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { eq } from "drizzle-orm";
import {
	ArrowLeft,
	Camera,
	Check,
	CheckCircle2,
	ChevronDown,
	Loader2,
	Search,
	Sparkles,
	UserCheck,
} from "lucide-react";
import { useCallback, useRef, useState } from "react";
import { getDb } from "@/db";
import { cars, events } from "@/db/schema";
import { getSession } from "@/lib/auth-session";
import { compressImage } from "@/lib/compress-image";

async function requireAdmin() {
	const session = await getSession();
	if (!session?.user) throw new Error("Unauthorized");
	const role = (session.user as { role?: string }).role;
	if (role !== "admin" && role !== "operator") throw new Error("Forbidden");
}

const getCheckinData = createServerFn({ method: "GET" })
	.inputValidator((eventId: string) => eventId)
	.handler(async ({ data: eventId }) => {
		const db = getDb();
		const event = await db
			.select()
			.from(events)
			.where(eq(events.id, Number(eventId)))
			.get();
		if (!event) throw new Error("Event not found");
		const eventCars = await db
			.select()
			.from(cars)
			.where(eq(cars.eventId, event.id));
		return { event, cars: eventCars };
	});

const toggleCheckin = createServerFn({ method: "POST" })
	.inputValidator((data: { carId: number; checkedIn: boolean }) => {
		if (!Number.isInteger(data.carId) || data.carId <= 0)
			throw new Error("Invalid carId");
		return data;
	})
	.handler(async ({ data }) => {
		await requireAdmin();
		await getDb()
			.update(cars)
			.set({ checkedIn: data.checkedIn, updatedAt: new Date() })
			.where(eq(cars.id, data.carId));
	});

const checkInAll = createServerFn({ method: "POST" })
	.inputValidator((eventId: number) => {
		if (!Number.isInteger(eventId) || eventId <= 0)
			throw new Error("Invalid eventId");
		return eventId;
	})
	.handler(async ({ data: eventId }) => {
		await requireAdmin();
		await getDb()
			.update(cars)
			.set({ checkedIn: true, updatedAt: new Date() })
			.where(eq(cars.eventId, eventId));
	});

const updateCarWeight = createServerFn({ method: "POST" })
	.inputValidator((data: { carId: number; weightOz: number | null }) => {
		if (!Number.isInteger(data.carId) || data.carId <= 0)
			throw new Error("Invalid carId");
		return data;
	})
	.handler(async ({ data }) => {
		await requireAdmin();
		await getDb()
			.update(cars)
			.set({ weightOz: data.weightOz, updatedAt: new Date() })
			.where(eq(cars.id, data.carId));
	});

const updateCarPhoto = createServerFn({ method: "POST" })
	.inputValidator((data: { carId: number; photoUrl: string }) => data)
	.handler(async ({ data }) => {
		await requireAdmin();
		await getDb()
			.update(cars)
			.set({ photoUrl: data.photoUrl, updatedAt: new Date() })
			.where(eq(cars.id, data.carId));
	});

const updateCarDetails = createServerFn({ method: "POST" })
	.inputValidator(
		(data: {
			carId: number;
			carName: string | null;
			division: string | null;
		}) => data,
	)
	.handler(async ({ data }) => {
		await requireAdmin();
		await getDb()
			.update(cars)
			.set({
				carName: data.carName,
				division: data.division,
				updatedAt: new Date(),
			})
			.where(eq(cars.id, data.carId));
	});

export const Route = createFileRoute("/admin/events/$eventId/checkin")({
	loader: ({ params }) => getCheckinData({ data: params.eventId }),
	component: CheckInPage,
});

function CheckInPage() {
	const params = Route.useParams();
	const initialData = Route.useLoaderData();
	const queryClient = useQueryClient();

	const { data } = useQuery({
		queryKey: ["checkin", params.eventId],
		queryFn: () => getCheckinData({ data: params.eventId }),
		initialData,
		refetchInterval: 3000,
	});

	const { event, cars: carList } = data;
	const [search, setSearch] = useState("");
	const [expandedCarId, setExpandedCarId] = useState<number | null>(null);
	const [uploadingCarId, setUploadingCarId] = useState<number | null>(null);
	const [generatingAvatarIds, setGeneratingAvatarIds] = useState<Set<number>>(
		new Set(),
	);
	const fileInputRefs = useRef<Map<number, HTMLInputElement>>(new Map());

	const divisions: string[] = (() => {
		try {
			return JSON.parse(event.divisions);
		} catch {
			return [];
		}
	})();

	const filteredCars = carList.filter(
		(car) =>
			(car.carName ?? "").toLowerCase().includes(search.toLowerCase()) ||
			car.driverName.toLowerCase().includes(search.toLowerCase()) ||
			String(car.carNumber).includes(search),
	);

	const sortedCars = [...filteredCars].sort((a, b) => {
		if (a.checkedIn !== b.checkedIn) return a.checkedIn ? 1 : -1;
		return a.carNumber - b.carNumber;
	});

	const checkedInCount = carList.filter((c) => c.checkedIn).length;
	const totalCount = carList.length;
	const progress =
		totalCount > 0 ? Math.round((checkedInCount / totalCount) * 100) : 0;

	const invalidate = useCallback(
		() =>
			queryClient.invalidateQueries({
				queryKey: ["checkin", params.eventId],
			}),
		[queryClient, params.eventId],
	);

	async function triggerAvatarGeneration(
		carId: number,
		carName: string | null,
	) {
		if (!carName) return;
		setGeneratingAvatarIds((prev) => new Set(prev).add(carId));
		try {
			await fetch("/api/generate-avatar", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ carId, carName }),
			});
		} finally {
			setGeneratingAvatarIds((prev) => {
				const next = new Set(prev);
				next.delete(carId);
				return next;
			});
			invalidate();
		}
	}

	async function handlePhotoCapture(carId: number, file: File) {
		setUploadingCarId(carId);
		try {
			const compressed = await compressImage(file);
			const formData = new FormData();
			formData.append("file", compressed);
			formData.append("carId", String(carId));
			const res = await fetch("/api/upload", {
				method: "POST",
				body: formData,
			});
			if (!res.ok) throw new Error("Upload failed");
			const { url } = (await res.json()) as { url: string };
			await updateCarPhoto({ data: { carId, photoUrl: url } });
			invalidate();

			const car = carList.find((c) => c.id === carId);
			triggerAvatarGeneration(carId, car?.carName ?? null);
		} finally {
			setUploadingCarId(null);
		}
	}

	async function handleToggle(carId: number, currentState: boolean) {
		await toggleCheckin({ data: { carId, checkedIn: !currentState } });
		invalidate();

		// On check-in, trigger avatar if car has name but no avatar
		if (!currentState) {
			const car = carList.find((c) => c.id === carId);
			if (car?.carName && !car.avatarUrl) {
				triggerAvatarGeneration(carId, car.carName);
			}
			if (expandedCarId === carId) {
				setTimeout(() => setExpandedCarId(null), 300);
			}
		}
	}

	async function handleCheckInAll() {
		await checkInAll({ data: event.id });
		invalidate();
	}

	async function handleWeightChange(carId: number, value: string) {
		const weight = value ? Number.parseFloat(value) : null;
		if (value && (Number.isNaN(weight!) || weight! < 0)) return;
		await updateCarWeight({ data: { carId, weightOz: weight } });
		invalidate();
	}

	async function handleCarDetailsChange(
		carId: number,
		carName: string | null,
		division: string | null,
	) {
		await updateCarDetails({ data: { carId, carName, division } });
		invalidate();
	}

	function toggleExpand(carId: number) {
		setExpandedCarId((prev) => (prev === carId ? null : carId));
	}

	return (
		<div className="min-h-screen bg-background p-4 sm:p-6">
			<div className="max-w-3xl mx-auto">
				<Link
					to="/admin/events/$eventId"
					params={{ eventId: String(event.id) }}
					className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground text-sm mb-4 transition-colors"
				>
					<ArrowLeft size={16} />
					Back to {event.name}
				</Link>

				<div className="flex items-center justify-between mb-6">
					<div>
						<h1 className="text-4xl text-foreground">Check-In</h1>
						<p className="text-muted-foreground">{event.name}</p>
					</div>
					{checkedInCount < totalCount && (
						<button
							type="button"
							onClick={handleCheckInAll}
							className="inline-flex items-center gap-1.5 px-3 py-2 bg-primary hover:bg-accent text-primary-foreground rounded-lg transition-colors text-sm font-bold uppercase"
						>
							<UserCheck size={14} />
							Check In All
						</button>
					)}
				</div>

				{/* Progress bar */}
				<div className="bg-card rounded-xl p-4 mb-6">
					<div className="flex items-center justify-between mb-2">
						<span className="text-sm font-bold text-foreground">
							{checkedInCount} / {totalCount} checked in
						</span>
						<span className="text-sm font-bold text-primary">{progress}%</span>
					</div>
					<div className="h-3 bg-muted rounded-full overflow-hidden">
						<div
							className="h-full bg-primary rounded-full transition-all duration-500"
							style={{ width: `${progress}%` }}
						/>
					</div>
					{divisions.length > 0 && (
						<div className="flex gap-4 mt-3 text-xs text-muted-foreground">
							{divisions.map((div) => {
								const divCars = carList.filter((c) => c.division === div);
								const divChecked = divCars.filter((c) => c.checkedIn).length;
								return (
									<span key={div}>
										{div}: {divChecked}/{divCars.length}
									</span>
								);
							})}
						</div>
					)}
				</div>

				{/* Search */}
				<div className="relative mb-4">
					<Search
						size={16}
						className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
					/>
					<input
						type="text"
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						placeholder="Search by name or number..."
						className="w-full pl-9 pr-4 py-3 text-base"
					/>
				</div>

				{/* Car list */}
				{sortedCars.length === 0 ? (
					<div className="bg-card rounded-xl p-12 text-center">
						<p className="text-foreground text-lg">
							{carList.length === 0
								? "No cars registered yet"
								: "No matching cars"}
						</p>
					</div>
				) : (
					<div className="space-y-2">
						{sortedCars.map((car) => {
							const isExpanded = expandedCarId === car.id;
							const isUploading = uploadingCarId === car.id;
							const isGeneratingAvatar = generatingAvatarIds.has(car.id);
							const thumb = car.avatarUrl || car.photoUrl;

							return (
								<div
									key={car.id}
									className={`bg-card/50 rounded-xl border-2 transition-all ${
										car.checkedIn
											? "border-success/40 bg-success/5"
											: "border-border"
									}`}
								>
									{/* Collapsed row */}
									<div className="flex items-center justify-between px-4 py-3">
										<div className="flex items-center gap-3 flex-1 min-w-0">
											<button
												type="button"
												onClick={() => handleToggle(car.id, car.checkedIn)}
												className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 transition-all ${
													car.checkedIn
														? "bg-success text-foreground"
														: "bg-muted text-muted-foreground hover:bg-primary hover:text-primary-foreground"
												}`}
											>
												{car.checkedIn ? (
													<CheckCircle2 size={20} />
												) : (
													<Check size={20} />
												)}
											</button>

											{thumb && (
												<img
													src={thumb}
													alt=""
													className="w-8 h-8 rounded-md object-cover shrink-0"
												/>
											)}

											<button
												type="button"
												onClick={() => toggleExpand(car.id)}
												className="min-w-0 text-left flex-1"
											>
												<p className="text-foreground font-bold">
													<span className="inline-flex items-center text-xs font-bold text-primary bg-primary/10 rounded px-1.5 py-0.5 mr-1.5">
														#{car.carNumber}
													</span>
													{car.carName}
												</p>
												<p className="text-muted-foreground text-sm truncate">
													{car.driverName}
													{car.division && (
														<span className="ml-1.5 px-1.5 py-0.5 bg-primary/10 text-primary text-xs font-bold rounded">
															{car.division}
														</span>
													)}
												</p>
											</button>
										</div>

										<div className="flex items-center gap-2 shrink-0">
											<div className="flex items-center gap-1.5">
												<input
													inputMode="decimal"
													step="0.01"
													min="0"
													defaultValue={car.weightOz ?? ""}
													key={`weight-${car.id}-${car.weightOz}`}
													onBlur={(e) =>
														handleWeightChange(car.id, e.target.value)
													}
													className="w-24 px-2 py-2.5 text-sm text-right font-display"
													placeholder={
														event.weightUnit === "oz" ? "5.00" : "141.7"
													}
												/>
												<span className="text-xs text-muted-foreground">
													{event.weightUnit}
												</span>
											</div>
											{car.weightOz != null &&
												car.weightOz > event.maxWeightOz && (
													<span className="text-destructive text-xs font-bold">
														Over!
													</span>
												)}
											<button
												type="button"
												onClick={() => toggleExpand(car.id)}
												className="p-1 text-muted-foreground hover:text-foreground transition-colors"
											>
												<ChevronDown
													size={18}
													className={`transition-transform duration-200 ${isExpanded ? "rotate-180" : ""}`}
												/>
											</button>
										</div>
									</div>

									{/* Expanded section */}
									<div
										className={`overflow-hidden transition-all duration-200 ${
											isExpanded
												? "max-h-60 opacity-100"
												: "max-h-0 opacity-0"
										}`}
									>
										<div className="px-4 pb-4 pt-1 border-t border-border/50 space-y-3">
											{/* Photo row */}
											<div className="flex items-center gap-3">
												{car.photoUrl ? (
													<img
														src={car.photoUrl}
														alt="Car photo"
														className="w-16 h-16 rounded-lg object-cover"
													/>
												) : (
													<div className="w-16 h-16 rounded-lg bg-muted flex items-center justify-center text-muted-foreground">
														<Camera size={24} />
													</div>
												)}
												<button
													type="button"
													disabled={isUploading}
													onClick={() =>
														fileInputRefs.current.get(car.id)?.click()
													}
													className="inline-flex items-center gap-1.5 px-3 py-2.5 bg-muted hover:bg-muted/80 text-foreground rounded-lg transition-colors text-sm min-w-[120px] min-h-[44px] justify-center"
												>
													{isUploading ? (
														<Loader2 size={16} className="animate-spin" />
													) : (
														<Camera size={16} />
													)}
													{isUploading ? "Uploading..." : "Take Photo"}
												</button>
												<input
													ref={(el) => {
														if (el) fileInputRefs.current.set(car.id, el);
														else fileInputRefs.current.delete(car.id);
													}}
													type="file"
													accept="image/*"
													capture="environment"
													className="hidden"
													onChange={(e) => {
														const file = e.target.files?.[0];
														if (file) handlePhotoCapture(car.id, file);
														e.target.value = "";
													}}
												/>
											</div>

											{/* Avatar status */}
											{(car.avatarUrl || isGeneratingAvatar) && (
												<div className="flex items-center gap-2">
													{car.avatarUrl && (
														<img
															src={car.avatarUrl}
															alt="Avatar"
															className="w-12 h-12 rounded-lg object-cover"
														/>
													)}
													{isGeneratingAvatar && (
														<span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
															<Loader2 size={12} className="animate-spin" />
															Generating avatar...
														</span>
													)}
												</div>
											)}
											{!car.avatarUrl &&
												!isGeneratingAvatar &&
												car.carName && (
													<button
														type="button"
														onClick={() =>
															triggerAvatarGeneration(car.id, car.carName)
														}
														className="inline-flex items-center gap-1.5 text-xs text-primary hover:text-accent transition-colors"
													>
														<Sparkles size={12} />
														Generate Avatar
													</button>
												)}

											{/* Car name */}
											<div className="flex items-center gap-2">
												<label className="text-xs text-muted-foreground uppercase font-bold w-20 shrink-0">
													Car Name
												</label>
												<input
													type="text"
													defaultValue={car.carName ?? ""}
													key={`name-${car.id}-${car.carName}`}
													onBlur={(e) => {
														const val = e.target.value.trim() || null;
														if (val !== (car.carName ?? null)) {
															handleCarDetailsChange(
																car.id,
																val,
																car.division ?? null,
															);
														}
													}}
													className="flex-1 px-2 py-1.5 text-sm"
													placeholder="Enter car name"
												/>
											</div>

											{/* Division */}
											{divisions.length > 0 && (
												<div className="flex items-center gap-2">
													<label className="text-xs text-muted-foreground uppercase font-bold w-20 shrink-0">
														Division
													</label>
													<select
														defaultValue={car.division ?? ""}
														key={`div-${car.id}-${car.division}`}
														onChange={(e) => {
															handleCarDetailsChange(
																car.id,
																car.carName ?? null,
																e.target.value || null,
															);
														}}
														className="flex-1 px-2 py-1.5 text-sm"
													>
														<option value="">None</option>
														{divisions.map((d) => (
															<option key={d} value={d}>
																{d}
															</option>
														))}
													</select>
												</div>
											)}
										</div>
									</div>
								</div>
							);
						})}
					</div>
				)}
			</div>
		</div>
	);
}
